import type { ProcessingType } from '@/types';
import { logger, devLog, ValidationError, ProcessingError, NetworkError } from '@/utils/logger';

const API_ENDPOINT = import.meta.env.VITE_AWS_API_ENDPOINT;
const API_KEY = import.meta.env.VITE_AWS_API_KEY;

if (!API_ENDPOINT) {
  throw new Error('VITE_AWS_API_ENDPOINT environment variable is required');
}

if (!API_KEY) {
  throw new Error('VITE_AWS_API_KEY environment variable is required');
}

interface PresignedUrlResponse {
  success: boolean;
  uploadUrl: string;
  s3Key: string;
  s3Bucket: string;
  error?: string;
}

interface AnalysisResponse {
  success: boolean;
  analysis: {
    processableRecords: number;
    invalidRecords: number;
    operation: string;
  };
  jobId?: string;
  error?: string;
}

interface StartProcessingPayload {
  s3Bucket: string;
  s3Key: string;
  jobId: string;
  operation: ProcessingType;
  companyId: string;
  userId: string;
  aiOverview?: boolean;
}

interface StartProcessingResponse {
  success: boolean;
  jobId: string;
  totalRecords?: number;
  message?: string;
  error?: string;
}

export interface JobStatusResponse {
  success: boolean;
  jobId: string;
  companyId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  progressPercentage?: number;
  currentStep: string; // "processing|completed|failed"
  processedRecords?: number;
  currentRecordIndex?: number;
  currentRecordBatch?: number;
  currentBatchSize?: number;
  currentRecordName?: string | null;
  lastProcessedRecord?: {
    recordIndex: number;
    name?: string | null;
    status: 'successful' | 'failed' | string;
    error?: string | null;
    completedAt?: string | null;
  } | null;
  lastProgressEventAt?: string;
  // Optional streaming metadata provided during in-flight processing
  dispatchedBatches?: number;
  plannedTotalBatches?: number;
  lastUpdatedAt?: string;

  // Summary is null until job completes
  summary: {
    totalRecords: number;
    successfulRecords: number;
    failedRecords: number;
    downloadUrl?: string; // Only present when status === "completed"
  } | null;

  // Billing information
  billing: {
    estimatedProcessable: number;
    actualProcessed: number;
    actualSuccessful: number;
    actualFailed: number;
  };

  // Top-level fields for legacy compatibility
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  createdAt: string; // ISO8601
  completedAt: string | null; // ISO8601 or null
  downloadUrl?: string; // Matches summary.downloadUrl when completed
  error: string | null;
}

interface JobSummaryResponse {
  success: boolean;
  jobId: string;
  companyId?: string;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  downloadUrl: string;
  expiresAt: string;
  error?: string;
}

export type ResultFormat = 'csv' | 'json' | (string & {});

export interface DownloadUrlResponse {
  success: boolean;
  downloadUrl: string;
  expiresAt: string;
  format?: ResultFormat;
  availableFormats?: ResultFormat[];
  contentType?: string;
  filename?: string;
  error?: string;
}

interface AccountInvitePayload extends Record<string, unknown> {
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  jobId?: string;
  processingType?: ProcessingType;
}

interface AccountInviteResponse {
  success: boolean;
  inviteSent?: boolean;
  requiresConfirmation?: boolean;
  autoConfirmed?: boolean;
  passwordSet?: boolean;
  userExists?: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
}

export class AWSAPIService {
  private companyId: string;

  constructor(companyId: string = 'CollectABILITY-Demo') {
    this.companyId = companyId;
  }

  private buildHeaders(additional?: Record<string, string>): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-internal-api-key': API_KEY as string,
      ...(additional ?? {}),
    };
  }

  private async post<T>(body: Record<string, unknown>): Promise<T> {
    // Add comprehensive request logging
    devLog.info('🔵 AWS API Request:', {
      endpoint: API_ENDPOINT,
      operation: body.operation || body.stage,
      body: body,
      timestamp: new Date().toISOString()
    });

    logger.debug('Posting to AWS API', { body });

    const response = await fetch(API_ENDPOINT as string, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    });

    const responseText = await response.text();

    // Log raw response
    devLog.info('🟢 AWS API Response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      body: responseText.substring(0, 500), // First 500 chars
      timestamp: new Date().toISOString()
    });

    if (!response.ok) {
      // Special handling for rate limit errors
      if (response.status === 429) {
        logger.warn('AWS API rate limit exceeded - polling too frequently');
        throw new NetworkError(
          'Rate limit exceeded. The system is polling too frequently. Retrying with backoff...',
          response.status,
          responseText,
        );
      }

      logger.error(`AWS API request failed: ${response.status} ${response.statusText}`, responseText);
      throw new NetworkError(
        `AWS request failed: ${response.status} ${response.statusText}`,
        response.status,
        responseText,
      );
    }

    try {
      const data = JSON.parse(responseText) as T;

      // Log parsed response
      devLog.info('✅ AWS API Parsed Response:', data);

      return data;
    } catch (error) {
      logger.error('Failed to parse AWS API response as JSON', error);
      throw new ProcessingError('AWS API returned an invalid response payload');
    }
  }

  /**
   * Step 1: Request a presigned upload URL for the incoming CSV file.
   */
  async getPresignedUploadUrl(filename: string): Promise<PresignedUrlResponse> {
    if (!filename) {
      throw new ValidationError('Filename is required for upload URL', 'filename');
    }

    logger.info(`Requesting presigned URL for file: ${filename}`);

    const response = await this.post<PresignedUrlResponse>({
      operation: 'get_upload_url',
      companyId: this.companyId,
      filename,
    });

    if (!response.success || !response.uploadUrl || !response.s3Key || !response.s3Bucket) {
      logger.error('Invalid presigned URL response from AWS', response.error);
      throw new ProcessingError(response.error ?? 'Failed to generate upload URL');
    }

    logger.info(`Presigned URL obtained: bucket=${response.s3Bucket}, key=${response.s3Key}`);
    return response;
  }

  /**
   * Step 2: Upload CSV file directly to S3 using the presigned URL.
   */
  async uploadFileToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    if (!uploadUrl) {
      throw new ValidationError('Upload URL is required', 'uploadUrl');
    }

    if (!file) {
      throw new ValidationError('File is required for S3 upload', 'file');
    }

    logger.info(`Uploading file to S3: ${file.name} (${file.size} bytes)`);

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable || !onProgress) {
          return;
        }
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          logger.info(`File uploaded successfully to S3: ${file.name}`);
          resolve();
          return;
        }

        const message = `S3 upload failed with status ${xhr.status}`;
        logger.error(message, xhr.responseText);
        reject(new NetworkError(message, xhr.status, xhr.responseText));
      });

      xhr.addEventListener('error', () => {
        const message = 'Network error during S3 upload';
        logger.error(message);
        reject(new NetworkError(message));
      });

      xhr.addEventListener('timeout', () => {
        const message = 'S3 upload timed out';
        logger.error(message);
        reject(new NetworkError(message));
      });

      xhr.open('PUT', uploadUrl);
      // Don't set Content-Type header - let the browser handle it or match what Lambda presigned URL expects
      // Setting Content-Type triggers CORS preflight, which S3 presigned URLs don't handle well
      xhr.timeout = 120_000;
      xhr.send(file);
    });
  }

  /**
   * Step 3: Ask backend to analyse the uploaded CSV so we know processable rows.
   */
  async requestAnalysis(
    s3Bucket: string,
    s3Key: string,
    operation: ProcessingType,
  ): Promise<AnalysisResponse> {
    if (!s3Bucket) {
      throw new ValidationError('S3 bucket is required for analysis', 's3Bucket');
    }

    if (!s3Key) {
      throw new ValidationError('S3 key is required for analysis', 's3Key');
    }

    if (!operation) {
      throw new ValidationError('Operation type is required for analysis', 'operation');
    }

    logger.info(`Requesting analysis: operation=${operation}, bucket=${s3Bucket}, key=${s3Key}`);

    const response = await this.post<AnalysisResponse>({
      operation: 'analyze_file',
      s3Bucket,
      s3Key,
      companyId: this.companyId,
      processingType: operation,
    });

    if (!response.success || !response.analysis) {
      logger.error('AWS API returned failure for analysis request:', response.error);
      throw new ProcessingError(response.error ?? 'Failed to analyse uploaded file');
    }

    return response;
  }

  /**
   * Combined helper used by FileUpload to run the full upload + analysis pipeline.
   */
  async uploadAndAnalyze(
    file: File,
    operation: ProcessingType,
    onProgress?: (message: string, progress: number) => void,
  ): Promise<{ analysis: AnalysisResponse['analysis']; s3Bucket: string; s3Key: string; jobId?: string }> {
    onProgress?.('Getting upload URL...', 10);
    const { uploadUrl, s3Bucket, s3Key } = await this.getPresignedUploadUrl(file.name);

    onProgress?.('Uploading file to S3...', 20);
    await this.uploadFileToS3(uploadUrl, file, (uploadProgress) => {
      const totalProgress = 20 + uploadProgress * 0.6;
      onProgress?.('Uploading file to S3...', Math.round(totalProgress));
    });

    onProgress?.('Starting analysis...', 85);
    const analysisData = await this.requestAnalysis(s3Bucket, s3Key, operation);

    onProgress?.('Analysis complete!', 100);

    return {
      analysis: analysisData.analysis,
      s3Bucket,
      s3Key,
      jobId: analysisData.jobId,
    };
  }

  async startProcessing(payload: StartProcessingPayload): Promise<StartProcessingResponse> {
    logger.info('Triggering backend batch execution', payload);
    devLog.info('Triggering backend batch execution with payload:', payload);

    const { aiOverview, ...rest } = payload;

    const response = await this.post<StartProcessingResponse>({
      stage: 'execute',
      approved: true,
      ...rest,
      ...(typeof aiOverview === 'boolean' ? { ai_overview: aiOverview } : {}),
    });

    // Log the raw response
    devLog.info('Backend startProcessing raw response:', response);

    if (!response.success) {
      devLog.error('Backend processing failed:', response.error);
      throw new ProcessingError(response.error ?? 'Failed to start batch processing', payload.jobId, 'execute');
    }

    return response;
  }

  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    if (!jobId) {
      throw new ValidationError('jobId is required to fetch status', 'jobId');
    }

    devLog.info('Fetching job status for:', jobId);

    const response = await this.post<JobStatusResponse>({
      operation: 'batch_status',
      jobId,
      companyId: this.companyId,
    });

    // Normalize progress for backwards compatibility with new backend fields
    const progressCandidates = [
      typeof response.progress === 'number' ? response.progress : undefined,
      typeof response.progressPercentage === 'number' ? response.progressPercentage : undefined,
      typeof response.processedRecords === 'number' &&
      typeof response.totalRecords === 'number' &&
      response.totalRecords > 0
        ? Math.round((response.processedRecords / response.totalRecords) * 100)
        : undefined,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    if (progressCandidates.length > 0) {
      const normalizedProgress = Math.min(100, Math.max(0, progressCandidates[0]));
      response.progress = normalizedProgress;
    } else {
      response.progress = 0;
    }

    devLog.info('Job status API response:', {
      jobId,
      success: response.success,
      status: response.status,
      error: response.error,
    });

    // Handle cases where success is undefined but we have valid status data
    if (response.success === false || (!response.success && !response.status)) {
      devLog.error('Job status fetch failed:', response.error);
      throw new ProcessingError(response.error ?? 'Failed to get job status', jobId);
    }

    return response;
  }

  async getJobSummary(jobId: string): Promise<JobSummaryResponse> {
    devLog.info('Fetching job summary for:', jobId);

    const response = await this.post<JobSummaryResponse>({
      operation: 'get_job_summary',
      jobId,
      companyId: this.companyId,
    });

    devLog.info('Job summary API response:', response);

    // Backend guarantees success boolean - handle failures properly
    if (!response.success) {
      throw new ProcessingError(response.error ?? 'Failed to retrieve job summary', jobId, 'summary');
    }

    response.jobId = response.jobId ?? jobId;
    return response;
  }

  async getCsvTemplate(): Promise<string> {
    const response = await this.post<{ success: boolean; csvTemplate?: string; csv?: string; downloadUrl?: string; error?: string }>({
      operation: 'csv_template',
      companyId: this.companyId,
    });

    if (!response.success) {
      throw new ProcessingError(response.error ?? 'Failed to retrieve CSV template');
    }

    if (response.csvTemplate) {
      return response.csvTemplate;
    }

    if (response.csv) {
      return response.csv;
    }

    if (response.downloadUrl) {
      const downloadResponse = await fetch(response.downloadUrl);
      if (!downloadResponse.ok) {
        throw new NetworkError(
          `Failed to download CSV template: ${downloadResponse.status} ${downloadResponse.statusText}`,
          downloadResponse.status,
        );
      }
      return await downloadResponse.text();
    }

    throw new ProcessingError('CSV template response missing template content');
  }


async requestAccountInvite(payload: AccountInvitePayload): Promise<AccountInviteResponse> {
  try {
    // Use dedicated account service instead of batch processing API
    const { accountServiceAPI } = await import('@/services/accountServiceAPI');

    const response = await accountServiceAPI.createAccountInvite(payload);
    return response;
  } catch (error) {
    logger.error('Failed to create account invite:', error);

    // Graceful fallback - don't break the user flow if account creation fails
    if (error instanceof NetworkError) {
      return {
        success: false,
        message: 'Account invitation could not be sent at this time. Please contact support to create an account.'
      };
    }

    throw error;
  }
}

  async getDownloadUrl(jobId: string, options?: { format?: 'csv' | 'json' }): Promise<DownloadUrlResponse> {
    devLog.info('Fetching download URL for:', { jobId, format: options?.format });

    const requestBody: Record<string, unknown> = {
      operation: 'get_download_url',
      jobId,
      companyId: this.companyId,
    };

    if (options?.format) {
      requestBody.format = options.format;
    }

    const response = await this.post<DownloadUrlResponse>(requestBody);

    devLog.info('Download URL API response:', response);

    // Backend guarantees success boolean and downloadUrl when successful
    if (!response.success) {
      throw new ProcessingError(response.error ?? 'Failed to retrieve download URL', jobId, 'get_download_url');
    }

    return response;
  }

  async downloadResults(jobId: string): Promise<Blob> {
    devLog.info('📥 Downloading results for job:', jobId);

    const { downloadUrl } = await this.getDownloadUrl(jobId);

    if (!downloadUrl) {
      throw new ProcessingError('Download URL missing from response', jobId, 'download_results');
    }

    const response = await fetch(downloadUrl);

    // Log response headers for debugging
    devLog.info('📥 Download response details:', {
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type'),
      contentDisposition: response.headers.get('content-disposition'),
      contentLength: response.headers.get('content-length'),
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      throw new NetworkError(`Failed to download job results: ${response.status} ${response.statusText}`, response.status);
    }

    const blob = await response.blob();

    devLog.info('📥 Downloaded blob details:', {
      type: blob.type,
      size: blob.size,
    });

    // Ensure blob is CSV type - fix if backend sends wrong content-type
    if (blob.type !== 'text/csv' && blob.type !== 'application/csv') {
      devLog.warn(`⚠️ Unexpected blob type: ${blob.type}, converting to CSV`);
      return new Blob([blob], { type: 'text/csv' });
    }

    return blob;
  }

  async cancelJob(jobId: string): Promise<boolean> {
    const response = await this.post<{ success: boolean; error?: string }>({
      operation: 'cancel_job',
      jobId,
      companyId: this.companyId,
    });

    if (!response.success) {
      throw new ProcessingError(response.error ?? 'Failed to cancel job', jobId, 'cancel_job');
    }

    return true;
  }

  async emailResults(jobId: string, recipientEmail: string): Promise<{ success: boolean; message?: string; error?: string }> {
    devLog.info('📧 Emailing results for job:', { jobId, recipientEmail });

    try {
      // Get download URL and job summary
      const [downloadResponse, summaryResponse] = await Promise.all([
        this.getDownloadUrl(jobId),
        this.getJobSummary(jobId),
      ]);

      if (!downloadResponse.downloadUrl) {
        throw new ProcessingError('Download URL not available', jobId, 'email_results');
      }

      // Import emailService dynamically to avoid circular dependencies
      const { emailService } = await import('./emailService');

      // Send email via SES
      const emailResult = await emailService.sendResultsEmail({
        recipientEmail,
        downloadUrl: downloadResponse.downloadUrl,
        jobSummary: {
          jobId: summaryResponse.jobId,
          successfulRows: summaryResponse.successfulRecords,
          failedRows: summaryResponse.failedRecords,
          totalProcessed: summaryResponse.totalRecords,
          fileName: downloadResponse.filename,
        },
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error ?? 'Failed to send email');
      }

      logger.info('Email sent successfully', {
        jobId,
        messageId: emailResult.messageId,
        recipient: recipientEmail,
      });

      return {
        success: true,
        message: `Results emailed successfully to ${recipientEmail}`,
      };
    } catch (error) {
      logger.error('Failed to email results', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email',
      };
    }
  }
}

export const awsAPI = new AWSAPIService();


